import type { ProjectEntry, ProviderKind, ServerProvider } from "@cmmd-forge/contracts";
import {
  PROVIDER_SEND_TURN_MAX_ATTACHMENTS,
  PROVIDER_SEND_TURN_MAX_IMAGE_BYTES,
} from "@cmmd-forge/contracts";

import type { ComposerTrigger } from "../../composer-logic";
import type { TerminalContextDraft } from "../../lib/terminalContext";
import { formatProviderSkillDisplayName } from "../../providerSkillPresentation";
import { searchProviderSkills } from "../../providerSkillSearch";
import { basenameOfPath } from "../../filePath";
import type { ComposerCommandItem } from "./ComposerCommandMenu";
import { builtInComposerSlashCommandItems } from "./composerBuiltInSlashCommands";
import { searchSlashCommandItems } from "./composerSlashCommandSearch";
import { isProviderAllowedByExecutionPolicy } from "../../providerPickerAvailability";
import type { CmmdOrgProviderPolicy } from "@cmmd-forge/contracts";

const IMAGE_SIZE_LIMIT_LABEL = `${Math.round(PROVIDER_SEND_TURN_MAX_IMAGE_BYTES / (1024 * 1024))}MB`;

/** Routes arrows through history only at the caret edge, giving active recall priority over menus. */
export function shouldNavigateComposerHistory(
  menuIsActive: boolean,
  historyIsActive: boolean,
  caretOnEdge: boolean,
): boolean {
  return caretOnEdge && (!menuIsActive || historyIsActive);
}

export function composerTriggersEqual(
  left: ComposerTrigger | null,
  right: ComposerTrigger | null,
): boolean {
  return (
    left === right ||
    (left !== null &&
      right !== null &&
      left.kind === right.kind &&
      left.query === right.query &&
      left.rangeStart === right.rangeStart &&
      left.rangeEnd === right.rangeEnd)
  );
}

export function extendReplacementRangeForTrailingSpace(
  text: string,
  rangeEnd: number,
  replacement: string,
): number {
  if (!replacement.endsWith(" ")) {
    return rangeEnd;
  }
  return text[rangeEnd] === " " ? rangeEnd + 1 : rangeEnd;
}

export function syncTerminalContextsByIds(
  contexts: ReadonlyArray<TerminalContextDraft>,
  ids: ReadonlyArray<string>,
): TerminalContextDraft[] {
  const contextsById = new Map(contexts.map((context) => [context.id, context]));
  return ids.flatMap((id) => {
    const context = contextsById.get(id);
    return context ? [context] : [];
  });
}

export function terminalContextIdListsEqual(
  contexts: ReadonlyArray<TerminalContextDraft>,
  ids: ReadonlyArray<string>,
): boolean {
  return (
    contexts.length === ids.length && contexts.every((context, index) => context.id === ids[index])
  );
}

export function buildComposerModelOptionsByProvider(
  providerStatuses: ReadonlyArray<Pick<ServerProvider, "provider" | "models">>,
  policy?: CmmdOrgProviderPolicy | null,
): Record<ProviderKind, ReadonlyArray<ServerProvider["models"][number]>> {
  const modelsForProvider = (
    provider: ProviderKind,
  ): ReadonlyArray<ServerProvider["models"][number]> =>
    isProviderAllowedByExecutionPolicy(provider, policy)
      ? (providerStatuses.find((candidate) => candidate.provider === provider)?.models ?? [])
      : [];

  return {
    codex: modelsForProvider("codex"),
    claudeAgent: modelsForProvider("claudeAgent"),
    ollama: modelsForProvider("ollama"),
    cmmd: modelsForProvider("cmmd"),
    apex: modelsForProvider("apex"),
    cursor: modelsForProvider("cursor"),
    opencode: modelsForProvider("opencode"),
  };
}

export function resolveSelectedModelForPicker(input: {
  modelOptionsByProvider: Record<ProviderKind, ReadonlyArray<ServerProvider["models"][number]>>;
  selectedProvider: ProviderKind;
  selectedModel: string;
  normalizeModelSlug: (model: string, provider: ProviderKind) => string | null;
}): string {
  const currentOptions = input.modelOptionsByProvider[input.selectedProvider];
  if (currentOptions.some((option) => option.slug === input.selectedModel)) {
    return input.selectedModel;
  }
  return (
    input.normalizeModelSlug(input.selectedModel, input.selectedProvider) ?? input.selectedModel
  );
}

export function buildComposerMenuSearchKey(trigger: ComposerTrigger | null): string | null {
  return trigger ? `${trigger.kind}:${trigger.query.trim().toLowerCase()}` : null;
}

export function getNextComposerMenuHighlightId(input: {
  items: ReadonlyArray<ComposerCommandItem>;
  highlightedItemId: string | null;
  direction: "ArrowDown" | "ArrowUp";
}): string | null {
  if (input.items.length === 0) {
    return input.highlightedItemId;
  }
  const highlightedIndex = input.items.findIndex((item) => item.id === input.highlightedItemId);
  const normalizedIndex =
    highlightedIndex >= 0 ? highlightedIndex : input.direction === "ArrowDown" ? -1 : 0;
  const offset = input.direction === "ArrowDown" ? 1 : -1;
  const nextIndex = (normalizedIndex + offset + input.items.length) % input.items.length;
  return input.items[nextIndex]?.id ?? null;
}

export function buildComposerMenuItems(input: {
  trigger: ComposerTrigger | null;
  workspaceEntries: ReadonlyArray<ProjectEntry>;
  searchableModelOptions: ReadonlyArray<{
    provider: ProviderKind;
    providerLabel: string;
    slug: string;
    name: string;
    searchSlug: string;
    searchName: string;
    searchProvider: string;
  }>;
  selectedProvider: ProviderKind;
  selectedProviderStatus: ServerProvider | undefined;
}): ComposerCommandItem[] {
  const { trigger } = input;
  if (!trigger) return [];

  if (trigger.kind === "path") {
    return input.workspaceEntries.map((entry) => ({
      id: `path:${entry.kind}:${entry.path}`,
      type: "path",
      path: entry.path,
      pathKind: entry.kind,
      label: basenameOfPath(entry.path),
      description: entry.parentPath ?? "",
    }));
  }

  if (trigger.kind === "slash-command") {
    const providerSlashCommandItems = (input.selectedProviderStatus?.slashCommands ?? []).map(
      (command) => ({
        id: `provider-slash-command:${input.selectedProvider}:${command.name}`,
        type: "provider-slash-command" as const,
        provider: input.selectedProvider,
        command,
        label: `/${command.name}`,
        description: command.description ?? command.input?.hint ?? "Run provider command",
      }),
    );
    const query = trigger.query.trim().toLowerCase();
    const builtInItems = builtInComposerSlashCommandItems(input.selectedProvider);
    const slashCommandItems = [...builtInItems, ...providerSlashCommandItems];
    return query ? searchSlashCommandItems(slashCommandItems, query) : slashCommandItems;
  }

  if (trigger.kind === "skill") {
    return searchProviderSkills(input.selectedProviderStatus?.skills ?? [], trigger.query).map(
      (skill) => ({
        id: `skill:${input.selectedProvider}:${skill.name}`,
        type: "skill" as const,
        provider: input.selectedProvider,
        skill,
        label: formatProviderSkillDisplayName(skill),
        description:
          skill.shortDescription ??
          skill.description ??
          (skill.scope ? `${skill.scope} skill` : "Run provider skill"),
      }),
    );
  }

  return input.searchableModelOptions
    .filter(({ searchSlug, searchName, searchProvider }) => {
      const query = trigger.query.trim().toLowerCase();
      if (!query) return true;
      return (
        searchSlug.includes(query) || searchName.includes(query) || searchProvider.includes(query)
      );
    })
    .map(({ provider, providerLabel, slug, name }) => ({
      id: `model:${provider}:${slug}`,
      type: "model",
      provider,
      model: slug,
      label: name,
      description: `${providerLabel} · ${slug}`,
    }));
}

export function getComposerMenuEmptyState(triggerKind: ComposerTrigger["kind"] | null): string {
  if (triggerKind === "skill") {
    return "No skills found. Try / to browse provider commands.";
  }
  return triggerKind === "path" ? "No matching files or folders." : "No matching command.";
}

export function isComposerPathMenuLoading(input: {
  triggerKind: ComposerTrigger["kind"] | null;
  pathTriggerQuery: string;
  isDebouncePending: boolean;
  isWorkspaceQueryLoading: boolean;
  isWorkspaceQueryFetching: boolean;
}): boolean {
  return (
    input.triggerKind === "path" &&
    ((input.pathTriggerQuery.length > 0 && input.isDebouncePending) ||
      input.isWorkspaceQueryLoading ||
      input.isWorkspaceQueryFetching)
  );
}

export function getComposerFooterActionLayoutKey(input: {
  activePendingProgress: {
    questionIndex: number;
    isLastQuestion: boolean;
  } | null;
  activePendingIsResponding: boolean;
  phase: string;
  showPlanFollowUpPrompt: boolean;
  prompt: string;
  hasSendableContent: boolean;
  isSendBusy: boolean;
  isConnecting: boolean;
  isPreparingWorktree: boolean;
}): string {
  if (input.activePendingProgress) {
    return `pending:${input.activePendingProgress.questionIndex}:${input.activePendingProgress.isLastQuestion}:${input.activePendingIsResponding}`;
  }
  if (input.phase === "running") {
    return "running";
  }
  if (input.showPlanFollowUpPrompt) {
    return input.prompt.trim().length > 0 ? "plan:refine" : "plan:implement";
  }
  return `idle:${input.hasSendableContent}:${input.isSendBusy}:${input.isConnecting}:${input.isPreparingWorktree}`;
}

export function shouldShowComposerImages(input: {
  isComposerApprovalState: boolean;
  pendingUserInputCount: number;
}): boolean {
  return !input.isComposerApprovalState && input.pendingUserInputCount === 0;
}

export function shouldShowPlanFollowUpPrimaryAction(input: {
  pendingUserInputCount: number;
  showPlanFollowUpPrompt: boolean;
}): boolean {
  return input.pendingUserInputCount === 0 && input.showPlanFollowUpPrompt;
}

export function getComposerPromptValue(input: {
  isComposerApprovalState: boolean;
  activePendingCustomAnswer: string | null;
  prompt: string;
}): string {
  if (input.isComposerApprovalState) {
    return "";
  }
  return input.activePendingCustomAnswer ?? input.prompt;
}

export type ComposerImageAdmission =
  | {
      accepted: true;
      file: File;
      fallbackName: string;
    }
  | {
      accepted: false;
      error: string;
      stop: boolean;
    };

export function evaluateComposerImageAdmission(input: {
  file: File;
  currentImageCount: number;
}): ComposerImageAdmission {
  if (!input.file.type.startsWith("image/")) {
    return {
      accepted: false,
      error: `Unsupported file type for '${input.file.name}'. Please attach image files only.`,
      stop: false,
    };
  }
  if (input.file.size > PROVIDER_SEND_TURN_MAX_IMAGE_BYTES) {
    return {
      accepted: false,
      error: `'${input.file.name}' exceeds the ${IMAGE_SIZE_LIMIT_LABEL} attachment limit.`,
      stop: false,
    };
  }
  if (input.currentImageCount >= PROVIDER_SEND_TURN_MAX_ATTACHMENTS) {
    return {
      accepted: false,
      error: `You can attach up to ${PROVIDER_SEND_TURN_MAX_ATTACHMENTS} images per message.`,
      stop: true,
    };
  }
  return {
    accepted: true,
    file: input.file,
    fallbackName: input.file.name || "image",
  };
}

export type PersistableComposerImageAttachment = {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  file: File;
};

export type PersistedComposerImageAttachmentSnapshot = {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  dataUrl: string;
};

export async function buildPersistedComposerImageAttachments(input: {
  images: ReadonlyArray<PersistableComposerImageAttachment>;
  existingPersistedAttachments: ReadonlyArray<PersistedComposerImageAttachmentSnapshot>;
  readFileAsDataUrl: (file: File) => Promise<string>;
}): Promise<PersistedComposerImageAttachmentSnapshot[]> {
  const existingPersistedById = new Map(
    input.existingPersistedAttachments.map((attachment) => [attachment.id, attachment]),
  );
  const stagedAttachmentById = new Map<string, PersistedComposerImageAttachmentSnapshot>();
  await Promise.all(
    input.images.map(async (image) => {
      try {
        const dataUrl = await input.readFileAsDataUrl(image.file);
        stagedAttachmentById.set(image.id, {
          id: image.id,
          name: image.name,
          mimeType: image.mimeType,
          sizeBytes: image.sizeBytes,
          dataUrl,
        });
      } catch {
        const existingPersisted = existingPersistedById.get(image.id);
        if (existingPersisted) {
          stagedAttachmentById.set(image.id, existingPersisted);
        }
      }
    }),
  );
  return Array.from(stagedAttachmentById.values());
}

export function filterPersistedComposerAttachmentsForImages(input: {
  images: ReadonlyArray<{ id: string }>;
  persistedAttachments: ReadonlyArray<PersistedComposerImageAttachmentSnapshot>;
}): PersistedComposerImageAttachmentSnapshot[] {
  const currentImageIds = new Set(input.images.map((image) => image.id));
  return input.persistedAttachments.filter((attachment) => currentImageIds.has(attachment.id));
}
